import { Box, Divider, Stack } from '@mui/material';
import { AzureOnboardingStatusStatusEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';

import CopyableCode from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';

import type { SetupOwnCloudState } from './SetupOwnCloudState';
import { BulletItem } from './bulletItem';
import { extractErrorMessage } from './errorHandling';
import { FormTextField } from './formTextField';
import { NumericBulletSection } from './numericBulletSection';
import { OwnCloudErrorDialog } from './ownCloudErrorDialog';
import { SetupAccountInProgress } from './setupAccountInProgress';

export const ConnectBackupAccount = (props: StepProps<SetupOwnCloudState>) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const dal = useDAL();
  const user = useUser();

  // Hardcoded eon control plane static IPs for GCP Prod deployment, TODO: receive the list from backend
  const code = `\
module "azure_backup" {
  source = "https://eon.blob.core.windows.net/public/onboarding/v1.1.2/terraform/backup.zip"

  subscription_id              = "${props.wizardState.backupSubscriptionId ?? ''}"
  tenant_id                    = "${props.wizardState.tenantId ?? ''}"
  management_app_id            = "${props.wizardState.managementApplicationId ?? ''}"
  backup_app_id                = "${props.wizardState.backupAppId ?? ''}"
  eon_account_id               = "${user.eonAccount?.id ?? ''}"
  project_id                   = "${user.currentProjectId ?? ''}"
  location                     = "${props.wizardState.regionName ?? ''}"
  eon_control_plane_static_ips = "35.237.123.147,34.139.17.58"
  adx_sku_name                 = "Standard_E4d_v5"
  adx_sku_capacity             = "2"
} `;

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);
    try {
      const response = await dal.onboarding.backupAccount.deploy({
        tenantId: props.wizardState.tenantId!,
        backupSubscriptionId: props.wizardState.backupSubscriptionId!,
        regionName: props.wizardState.regionName!,
        adxClusterId: props.wizardState.adxClusterId!,
        adxClusterIdentity: props.wizardState.adxClusterIdentity!,
        adxClusterUri: props.wizardState.adxClusterUri!,
      });

      // Check if the response has a status field and it's not succeeded
      if (
        response &&
        response.status !== AzureOnboardingStatusStatusEnum.Succeeded
      ) {
        console.log(response);
        setError(new Error("Failed to deploy Eon's backup account"));
      } else {
        setIsDeploying(false);
        props.setNextStep(props.currentStep.next?.[0]);
      }
    } catch (error) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
      setIsDeploying(false);
    }
  };

  return (
    <>
      <StepContainer
        stepperIndex={1}
        stepperLabels={props.stepperLabels}
        canGoNext={() =>
          !isDeploying &&
          !!props.wizardState.backupSubscriptionId &&
          !!props.wizardState.regionName &&
          !!props.wizardState.adxClusterId &&
          !!props.wizardState.adxClusterIdentity &&
          !!props.wizardState.adxClusterUri
        }
        nextButtonDisabledTooltip={
          isDeploying ? 'Please wait for the deployment to complete' : undefined
        }
        nextButtonText={'Connect & Continue'}
        sx={{ overflow: 'auto' }}
        onNextClick={onNextClick}
      >
        <Box paddingX='min(100px,6vw)' width='100%' height='auto'>
          {isDeploying ? (
            <SetupAccountInProgress />
          ) : (
            <Stack
              gap='40px'
              divider={<Divider sx={{ borderStyle: 'dashed' }} />}
            >
              <NumericBulletSection
                bulletNumber={1}
                title={`Deploy Eon's backup account`}
              >
                <BulletItem
                  title='Fill in the details to generate your Terraform module, then review and deploy it in your environment.'
                  leftContent={
                    <Stack gap='24px'>
                      <FormTextField
                        title='Subscription ID'
                        placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                        value={props.wizardState.backupSubscriptionId || ''}
                        onChange={(event) =>
                          props.setWizardState({
                            ...props.wizardState,
                            backupSubscriptionId: event.target.value,
                          })
                        }
                      />
                    </Stack>
                  }
                  rightContent={<CopyableCode title='main.tf' code={code} />}
                />
              </NumericBulletSection>
              <NumericBulletSection
                bulletNumber={2}
                title='Complete configuration'
              >
                <BulletItem
                  title='Bring back the Terraform outputs to complete this step.'
                  leftContent={
                    <Stack gap='24px'>
                      <FormTextField
                        title='adx_cluster_id'
                        placeholder='Example: /subscriptions/12345678-90ab-cdef-1234-567890abcdef/resourceGroups/eon/providers/Microsoft.Kusto/clusters/eon'
                        value={props.wizardState.adxClusterId || ''}
                        onChange={(event) =>
                          props.setWizardState({
                            ...props.wizardState,
                            adxClusterId: event.target.value,
                          })
                        }
                      />
                      <FormTextField
                        title='adx_cluster_identity'
                        placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                        value={props.wizardState.adxClusterIdentity || ''}
                        onChange={(event) =>
                          props.setWizardState({
                            ...props.wizardState,
                            adxClusterIdentity: event.target.value,
                          })
                        }
                      />
                      <FormTextField
                        title='adx_cluster_uri'
                        placeholder='Example: https://eon.eastus2.kusto.windows.net'
                        value={props.wizardState.adxClusterUri || ''}
                        onChange={(event) =>
                          props.setWizardState({
                            ...props.wizardState,
                            adxClusterUri: event.target.value,
                          })
                        }
                      />
                    </Stack>
                  }
                />
              </NumericBulletSection>
            </Stack>
          )}
        </Box>
      </StepContainer>
      <OwnCloudErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
